import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';

/**
 * Cookie Consent Banner
 *
 * GDPR/CCPA compliant cookie consent banner.
 * Shows on first visit, stores preference in localStorage.
 * Controls loading of analytics and marketing cookies.
 */

const CONSENT_KEY = 'cookie_consent';
const CONSENT_VERSION = 1; // Increment to re-ask consent

/**
 * Get current consent status
 */
export const getConsentStatus = () => {
  try {
    const stored = localStorage.getItem(CONSENT_KEY);
    if (!stored) return null;

    const consent = JSON.parse(stored);
    // Check if consent version matches
    if (consent.version !== CONSENT_VERSION) return null;

    return consent;
  } catch {
    return null;
  }
};

/**
 * Check if analytics consent was given
 */
export const hasAnalyticsConsent = () => {
  const consent = getConsentStatus();
  return consent?.analytics === true;
};

/**
 * Check if marketing consent was given
 */
export const hasMarketingConsent = () => {
  const consent = getConsentStatus();
  return consent?.marketing === true;
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false,
  });

  useEffect(() => {
    // Check if user has already consented
    const consent = getConsentStatus();
    if (!consent) {
      // Small delay to prevent flash on page load
      const timer = setTimeout(() => setIsVisible(true), 1000);
      return () => clearTimeout(timer);
    }
  }, []);

  const saveConsent = (acceptAll = false) => {
    const consent = {
      version: CONSENT_VERSION,
      timestamp: new Date().toISOString(),
      necessary: true,
      analytics: acceptAll ? true : preferences.analytics,
      marketing: acceptAll ? true : preferences.marketing,
    };

    localStorage.setItem(CONSENT_KEY, JSON.stringify(consent));
    setIsVisible(false);

    // Reload to apply consent (GA will check consent on load)
    if (consent.analytics) {
      // Enable GA tracking
      if (window.gtag) {
        window.gtag('consent', 'update', {
          analytics_storage: 'granted',
        });
      }
    }
  };

  const handleAcceptAll = () => {
    saveConsent(true);
  };

  const handleAcceptSelected = () => {
    saveConsent(false);
  };

  const handleRejectAll = () => {
    setPreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
    setTimeout(() => saveConsent(false), 0);
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 z-50 p-4 bg-white border-t-2 border-gray-200 shadow-2xl">
      <div className="max-w-6xl mx-auto">
        {/* Main Banner */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div className="flex-1">
            <h3 className="font-bold text-gray-800 mb-1">We value your privacy</h3>
            <p className="text-sm text-gray-600">
              We use cookies to enhance your experience, analyze site traffic, and for marketing purposes.
              By clicking "Accept All", you consent to our use of cookies.{' '}
              <Link to="/privacy" className="text-indigo-600 hover:underline">
                Privacy Policy
              </Link>
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 underline"
            >
              {showDetails ? 'Hide Details' : 'Customize'}
            </button>
            <button
              onClick={handleRejectAll}
              className="px-4 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Reject All
            </button>
            <button
              onClick={handleAcceptAll}
              className="px-6 py-2 text-sm bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Accept All
            </button>
          </div>
        </div>

        {/* Details Panel */}
        {showDetails && (
          <div className="mt-4 pt-4 border-t border-gray-200">
            <div className="grid md:grid-cols-3 gap-4">
              {/* Necessary Cookies */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Necessary</span>
                  <span className="text-xs text-gray-500 bg-gray-200 px-2 py-0.5 rounded">Always On</span>
                </div>
                <p className="text-xs text-gray-600">
                  Essential for the website to function. Cannot be disabled.
                </p>
              </div>

              {/* Analytics Cookies */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Analytics</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.analytics}
                      onChange={(e) => setPreferences(p => ({ ...p, analytics: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-600">
                  Help us understand how you use our site to improve your experience.
                </p>
              </div>

              {/* Marketing Cookies */}
              <div className="p-3 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-medium text-gray-800">Marketing</span>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={preferences.marketing}
                      onChange={(e) => setPreferences(p => ({ ...p, marketing: e.target.checked }))}
                      className="sr-only peer"
                    />
                    <div className="w-9 h-5 bg-gray-300 peer-focus:outline-none peer-focus:ring-2 peer-focus:ring-indigo-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-indigo-600"></div>
                  </label>
                </div>
                <p className="text-xs text-gray-600">
                  Used for personalized ads and measuring ad effectiveness.
                </p>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={handleAcceptSelected}
                className="px-6 py-2 text-sm bg-gray-800 text-white font-semibold rounded-lg hover:bg-gray-900 transition-colors"
              >
                Save Preferences
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieConsent;
